/**
 * Discord integration data layer: connected account, links, and the wizard's
 * guild/channel data. REST-only (Discord state lives server-side); the
 * fetch-and-cache shape follows taskStats.
 */
import { create } from "zustand";
import type { DiscordLinkDto, DiscordWhitelistEntryDto } from "@hydra-notes/shared";

export interface DiscordAccountState {
  configured: boolean;
  connected: boolean;
  account?: { discordUserId: string; username: string; globalName: string | null; avatarUrl: string | null };
  guildsWarm?: boolean;
}

export interface WizardGuild {
  id: string;
  name: string;
  iconUrl: string | null;
  botPresent: boolean;
  inviteUrl?: string;
}

export interface WizardChannel {
  id: string;
  name: string;
  missingPerms: string[];
  linked: boolean;
}

async function api<T>(path: string, init?: RequestInit): Promise<{ ok: boolean; status: number; body: T | null }> {
  try {
    const res = await fetch(path, {
      credentials: "same-origin",
      ...init,
      headers: { ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers },
    });
    return { ok: res.ok, status: res.status, body: (await res.json().catch(() => null)) as T | null };
  } catch {
    return { ok: false, status: 0, body: null };
  }
}

interface DiscordState {
  account: DiscordAccountState | null;
  links: DiscordLinkDto[];
  linksLoaded: boolean;
  refreshAccount: () => Promise<void>;
  refreshLinks: () => Promise<void>;
  fetchGuilds: () => Promise<{ guilds: WizardGuild[] } | { error: string; reconnect?: boolean }>;
  fetchChannels: (guildId: string) => Promise<{ channels: WizardChannel[] } | { error: string }>;
  createLink: (body: {
    listId: string;
    spaceId?: string;
    guildId: string;
    channelId: string;
    backfill: boolean;
  }) => Promise<{ link: DiscordLinkDto; warning?: string } | { error: string; reconnect?: boolean }>;
  patchLink: (id: string, body: { buttonsEnabled: boolean }) => Promise<void>;
  verifyLink: (id: string) => Promise<void>;
  deleteLink: (id: string) => Promise<void>;
  disconnect: () => Promise<void>;
  fetchWhitelist: (linkId: string) => Promise<DiscordWhitelistEntryDto[] | null>;
  addWhitelist: (linkId: string, kind: "user" | "role", discordId: string) => Promise<boolean>;
  removeWhitelist: (linkId: string, kind: string, discordId: string) => Promise<void>;
  search: (
    guildId: string,
    q: string,
  ) => Promise<{
    members: { id: string; name: string; avatarUrl: string | null }[];
    roles: { id: string; name: string; color: string | null }[];
  } | null>;
}

export const useDiscord = create<DiscordState>((set, get) => ({
  account: null,
  links: [],
  linksLoaded: false,

  refreshAccount: async () => {
    const res = await api<DiscordAccountState>("/api/discord/account");
    if (res.body) set({ account: res.body });
  },

  refreshLinks: async () => {
    const res = await api<{ links: DiscordLinkDto[] }>("/api/discord/links");
    if (res.body && "links" in res.body) set({ links: res.body.links, linksLoaded: true });
  },

  fetchGuilds: async () => {
    const res = await api<{ guilds?: WizardGuild[]; error?: string }>("/api/discord/guilds");
    if (res.ok && res.body?.guilds) return { guilds: res.body.guilds };
    if (res.status === 409) return { error: "reconnect", reconnect: true };
    return { error: res.body?.error ?? "could not load your servers" };
  },

  fetchChannels: async (guildId) => {
    const res = await api<{ channels?: WizardChannel[]; error?: string }>(
      `/api/discord/guilds/${encodeURIComponent(guildId)}/channels`,
    );
    if (res.ok && res.body?.channels) return { channels: res.body.channels };
    return { error: res.body?.error ?? "could not load channels" };
  },

  createLink: async (body) => {
    const res = await api<{ link?: DiscordLinkDto; warning?: string; error?: string }>("/api/discord/links", {
      method: "POST",
      body: JSON.stringify(body),
    });
    if (res.ok && res.body?.link) {
      void get().refreshLinks();
      return { link: res.body.link, ...(res.body.warning ? { warning: res.body.warning } : {}) };
    }
    if (res.status === 409 && res.body?.error === "reconnect_required") return { error: "reconnect", reconnect: true };
    return { error: res.body?.error ?? "could not create the link" };
  },

  patchLink: async (id, body) => {
    await api(`/api/discord/links/${id}`, { method: "PATCH", body: JSON.stringify(body) });
    void get().refreshLinks();
  },

  verifyLink: async (id) => {
    await api(`/api/discord/links/${id}/verify`, { method: "POST", body: "{}" });
    void get().refreshLinks();
  },

  deleteLink: async (id) => {
    await api(`/api/discord/links/${id}`, { method: "DELETE" });
    void get().refreshLinks();
  },

  disconnect: async () => {
    await api("/api/discord/account", { method: "DELETE" });
    void get().refreshAccount();
  },

  fetchWhitelist: async (linkId) => {
    const res = await api<{ entries: DiscordWhitelistEntryDto[] }>(`/api/discord/links/${linkId}/whitelist`);
    return res.body && "entries" in res.body ? res.body.entries : null;
  },

  addWhitelist: async (linkId, kind, discordId) => {
    const res = await api(`/api/discord/links/${linkId}/whitelist`, {
      method: "POST",
      body: JSON.stringify({ kind, discordId }),
    });
    void get().refreshLinks();
    return res.ok;
  },

  removeWhitelist: async (linkId, kind, discordId) => {
    await api(`/api/discord/links/${linkId}/whitelist/${kind}/${encodeURIComponent(discordId)}`, { method: "DELETE" });
    void get().refreshLinks();
  },

  search: async (guildId, q) => {
    const res = await api<{
      members: { id: string; name: string; avatarUrl: string | null }[];
      roles: { id: string; name: string; color: string | null }[];
    }>(`/api/discord/guilds/${encodeURIComponent(guildId)}/search?q=${encodeURIComponent(q)}`);
    return res.ok ? res.body : null;
  },
}));

/** One-shot wizard continuity across the OAuth redirect. */
const OAUTH_PENDING_KEY = "hn.discordWizardPending";

export function stashWizardState(state: { listId?: string; spaceId?: string }): void {
  sessionStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(state));
}

export function consumeWizardState(): { listId?: string; spaceId?: string } | null {
  const raw = sessionStorage.getItem(OAUTH_PENDING_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(OAUTH_PENDING_KEY);
  try {
    return JSON.parse(raw) as { listId?: string; spaceId?: string };
  } catch {
    return null;
  }
}
